import React, { useState, useEffect } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { i18nIntl, formatCurrencyPrice } from '../../lib/ep';
import { useOwner } from '../context/context';

import ButtonCollection from '../builder/ButtonCollection';
import FloatingLayer from '../builder/FloatingLayer';
import SectionTitle from '../builder/SectionTitle';
import Spinner from '../builder/Spinner';
import IconedSelectorItems from '../wrappers/IconedSelectorItems';

import ChargeBee from './ChargeBee';
import { AlertMessage } from '../builder/Message';

const TiersLegend = ({ pack, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const tiers = pack?.tiers ?? [];

	if(tiers.length == 0) return null;

	const unit_label = props?.unit_label ?? { singular:i18n("Unit"), plural:i18n("Units") };

	return (
		<ul class="tiers_legend gui_small_text gui_not_in_top">
			{pack.tiers.map((tier, t) => {
				const min = tier.starting_unit;
				const max = (tier.ending_unit == undefined || tier.ending_unit == '') ? 99999 : tier.ending_unit;

				return (
					<li key={`tier_${t}`} class="gui_flexbox_auto gui_flex_space_between gui_not_in_top_tiny" style={{ gap:'var(--grid-1)' }}>
						<FormattedMessage id="From {min} to {max} {item}" values={{ min, max, item:unit_label.plural }} />

						<span style={{ borderBottom:'1px dotted rgba(var(--main-text-color), 0.2)', flexGrow:'1' }} />

						<span>{parseFloat(tier.price)} {i18n("cts")} / {unit_label.singular}</span>
					</li>
				)
			})}
		</ul>
	)
};

const TiersSelector = ({ pack, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ model, setModel ] = useState({ units:'' });

	const avail_units = props?.avail_units ?? false;
	const display_tiers = props?.display_tiers ?? true;
	const tiers = pack?.tiers ?? [];

	if(tiers.length == 0 || avail_units === false) {
		return (
			<div>{i18n("There has been an error")}</div>
		)
	}

	const unit_label = props?.unit_label ?? { singular:i18n("Unit"), plural:i18n("Units") };

	const onSelectPack = (k, v) => {
		setModel({ ...model, [k]:v });

		props.onSelectPack(((v != '----') ? pack : false), ((v != '----') ? v : 1), single_pack_prices[v]);
	};

	let single_pack_prices = { '----':false };
	let options = [{ k:'----', v:`-- ${i18n("Select a pack")} --` }];
	let list_options = [];
	avail_units.map((unit) => {
		const _unit_price = tiers.find((t) => {
			if(parseInt(t.starting_unit) > unit) return false;
			if(parseInt(t.ending_unit) < unit) return false;
			return true;
		});
		if(_unit_price == undefined) return null;
		
		const unit_price = parseFloat(_unit_price.price);
		const option_prize = Math.round(0.01 * unit_price * unit);
		const concept = props.intl.formatMessage({ id:"Pack of {total} {item}" }, { total:unit.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ","), item:(unit > 1) ? unit_label.plural : unit_label.singular });

		const item_price = formatCurrencyPrice(option_prize, props.currency)
		single_pack_prices[unit] = item_price;

		options.push({ k:unit, v:`${concept}` });
		list_options.push({
			key:unit,
			name:concept,
			desc:item_price,
			cta_label:"Select pack",
			cta_label_selected:"Pack selected",
			onClick:() => onSelectPack('units', unit),
			icon:'knk-arrow-r-big',
		});
	});

	return (
		<div>
			<IconedSelectorItems
				items={list_options}
				selected_item={model.units}
			/>

			{/*<Model model={model} change={onSelectPack}>
				<Select
					name='units'
					values={options}
					autotranslate={false}
					_class={{ first:true, size:50 }}
				>{props.intl.formatMessage({ id:"{type} package" }, { type:unit_label.plural })}</Select>
			</Model>*/}

			{(display_tiers) && (
				<TiersLegend
					pack={pack}
					unit_label={unit_label}
					intl={props.intl}
				/>
			)}
		</div>
	)
};

const PackSelectionLayer = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const owner = useOwner();

	const [ buy_item, setBuyItem ] = useState(false);
	const [ packs, setPacks ] = useState([]);
	const [ quantity, setQuantity ] = useState(1);
	const [ pack_price, setPackPrice ] = useState(false);

	useEffect(() => {
		$.post(`/payments/get_items/`, { item:props.pack_type }, (res) => {
			setPacks(res);
		}, 'JSON');
	}, []);

	const selectPack = (pack, units, final_price) => {
		setQuantity(units);
		setBuyItem(pack);
		setPackPrice(final_price);
	};

	const onSuccess = () => {
		if (props.onPurchase != undefined) {
			props.onPurchase(quantity)
		}
	};

	if(props.details) {
		return (
			<div>
				{JSON.stringify(props.details)}
			</div>
		)
	}

	const unit_label = props?.unit_label ?? { singular:i18n("Unit"), plural:i18n("Units") };
	const conditions = props?.conditions ?? [];
	const display_tiers = props?.display_tiers ?? false;

	// This only applies in the layer display.
	// If user changes any of these window variables without having the profile to enable this,
	// the server will block it anyway before letting the user pay.
	const server_type = window?.server_type ?? '0';
	const is_agent_role_admin = window?.is_agent_role_admin ?? false;
	const is_admin = window?.is_admin ?? false;
	let is_enabled = false;
	if(is_agent_role_admin && server_type == '0') is_enabled = true;	// if user is promotion administrator (and ONLY in production)
	if(is_admin && server_type != '0') is_enabled = true;			// if user is superadmin (and ONLY NOT in production)

	if(!is_enabled) {
		return (
			<FloatingLayer _class={{ size:'small' }} backButton={props.close}>
				<div class="without_actions">
					<SectionTitle>{props?.title ?? "Package purchase"}</SectionTitle>

					<AlertMessage
						title="Action not permitted"
						_class={{ other:'in_top in_bottom' }}
					>
						<p><strong>{i18n("Only account administrators can purchase SMS packages.")}</strong></p>
						<p>{i18n("Please contact the account administrator.")}</p>
					</AlertMessage>
				</div>
			</FloatingLayer>
		)
	}

	return (
		<FloatingLayer _class={{ size:'small' }} backButton={props.close}>
			<div>
				<SectionTitle>{props?.title ?? "Package purchase"}</SectionTitle>

				{(conditions.length > 0) && (
					<ul class="pack_conditions bulleted_list gui_small_text gui_not_in_bottom bulleted_list">
						{conditions.map((condition, c) => {
							return (
								<li key={`condition_${c}`}>{i18n(condition)}</li>
							)
						})}
					</ul>
				)}

				{(packs.length == 0) && (
					<Spinner>Loading packages</Spinner>
				)}

				{packs.length > 0 && packs.map((pack) => {
					return (
						<TiersSelector
							key={`pack_${pack.id}`}
							pack={pack}
							currency={window.currency}
							avail_units={props.avail_units}
							onSelectPack={selectPack}
							unit_label={unit_label}
							display_tiers={display_tiers}
							intl={props.intl}
						/>
					)}
				)}

				<ButtonCollection in_floating={true}>
					<ChargeBee
						account_id={owner.account.id}
						item_id={buy_item.id}
						type={buy_item.item_type}
						quantity={quantity}
						autotranslate={false}
						onSuccess={onSuccess}
					>{(pack_price != false) ? `${i18n("Buy pack")}: ${pack_price}`: i18n("Buy pack")}</ChargeBee>
				</ButtonCollection>
			</div>
		</FloatingLayer>
	)
};

export default injectIntl(PackSelectionLayer);